// Portal session — server-authoritative (v3 Displet auth boundary).
//
// Authentication is NO LONGER a client-writable flag. The only source of truth is
// the Worker's signed httpOnly session cookie, which the browser can't read or
// forge; we learn our auth state by asking the server (`GET /api/portal/session`)
// and change it only through the server (`POST /api/portal/auth[/mfa]`,
// `/portal/logout`). Brand/space are UI preferences (which space you're reading)
// and stay in localStorage — they are not authorization.

import { BRAND_ORDER, type BrandId } from "./brands";

const API = (import.meta as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ?? "/api";

const VALID_BRANDS = new Set<string>(BRAND_ORDER);
function coerceBrand(v: unknown): BrandId {
  return typeof v === "string" && VALID_BRANDS.has(v) ? (v as BrandId) : "recursive";
}

/** The server's verdict about the current session. */
export interface ServerSession {
  authed: boolean;
  network?: string; // Displet network UUID the member signed into
  name?: string;
  org?: string; // the network's display name
}

/** Ask the server whether this browser holds a valid session. Never throws. */
export async function fetchSession(): Promise<ServerSession> {
  try {
    const res = await fetch(`${API}/portal/session`, { credentials: "include", headers: { accept: "application/json" } });
    if (!res.ok) return { authed: false };
    const j = (await res.json()) as ServerSession;
    return { authed: !!j.authed, network: j.network, name: j.name, org: j.org };
  } catch {
    return { authed: false };
  }
}

// A tiny shared cache so the route gate (beforeLoad) and the provider don't each
// hit /portal/session on the same navigation. Short TTL; cleared on sign-in/out.
let cache: { at: number; val: ServerSession } | null = null;
let inflight: Promise<ServerSession> | null = null;
// Bumped whenever the session state changes (force refresh / sign-out). A fetch
// captures the epoch at start and only writes `cache` if it's STILL current — so a
// request started before an invalidation can never install a stale verdict.
let epoch = 0;
export async function getSession(force = false): Promise<ServerSession> {
  if (force) {
    cache = null;
    inflight = null;
    epoch++;
  }
  if (!force && cache && Date.now() - cache.at < 5000) return cache.val;
  if (!inflight) {
    const myEpoch = epoch;
    const p: Promise<ServerSession> = fetchSession().then((v) => {
      if (myEpoch === epoch) {
        cache = { at: Date.now(), val: v };
        if (inflight === p) inflight = null;
      }
      return v;
    });
    inflight = p;
  }
  return inflight;
}
export function clearSessionCache(): void {
  cache = null;
  inflight = null;
  epoch++;
}

export type SignInResult = { ok: true; name?: string } | { ok: true; mfaRequired: true; mfaToken: string } | { ok: false; error: string; notConfigured?: boolean };

/** Exchange credentials for a session (POST /portal/auth). Never throws. */
export async function signInPortal(networkUid: string, login: string, password: string, remember: boolean): Promise<SignInResult> {
  try {
    const res = await fetch(`${API}/portal/auth`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ networkUid, login, password, remember }),
    });
    const j = (await res.json().catch(() => ({}))) as { ok?: boolean; name?: string; mfa_required?: boolean; mfa_token?: string; error?: string };
    if (res.status === 202 && j.mfa_required && j.mfa_token) return { ok: true, mfaRequired: true, mfaToken: j.mfa_token };
    if (res.ok && j.ok) return { ok: true, name: j.name };
    return { ok: false, error: j.error ?? "sign_in_failed", notConfigured: j.error === "auth_not_configured" };
  } catch {
    return { ok: false, error: "network_error" };
  }
}

/** Complete an MFA challenge (POST /portal/auth/mfa). Never throws. */
export async function completeMfa(mfaToken: string, code: string, remember: boolean): Promise<SignInResult> {
  try {
    const res = await fetch(`${API}/portal/auth/mfa`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mfaToken, code, remember }),
    });
    const j = (await res.json().catch(() => ({}))) as { ok?: boolean; name?: string; error?: string };
    if (res.ok && j.ok) return { ok: true, name: j.name };
    return { ok: false, error: j.error ?? "mfa_failed" };
  } catch {
    return { ok: false, error: "network_error" };
  }
}

/** The network picker options (GET /portal/networks). Never throws. */
export async function fetchNetworks(): Promise<{ configured: boolean; networks: { uid: string; name: string }[] }> {
  try {
    const res = await fetch(`${API}/portal/networks`, { credentials: "include", headers: { accept: "application/json" } });
    if (!res.ok) return { configured: false, networks: [] };
    const j = (await res.json()) as { configured?: boolean; networks?: { uid: string; name: string }[] };
    return { configured: !!j.configured, networks: Array.isArray(j.networks) ? j.networks : [] };
  } catch {
    return { configured: false, networks: [] };
  }
}

/** End the session on the server. Never throws. */
export async function signOutPortal(): Promise<void> {
  try {
    await fetch(`${API}/portal/logout`, { method: "POST", credentials: "include" });
  } catch {
    /* best-effort */
  }
}

/* ------------------------------------------------------------------ *
 * Brand / space are UI preferences (NOT authorization) — safe in localStorage.
 * ------------------------------------------------------------------ */
const SCOPE_KEY = "portal:scope";
export interface PortalScope {
  brandId: BrandId;
  spaceId: string;
}
const DEFAULT_SCOPE: PortalScope = { brandId: "recursive", spaceId: "platform" };

export function loadScope(): PortalScope {
  try {
    const raw = globalThis.localStorage?.getItem(SCOPE_KEY);
    if (!raw) return { ...DEFAULT_SCOPE };
    const v = JSON.parse(raw) as Partial<PortalScope>;
    return { brandId: coerceBrand(v.brandId), spaceId: typeof v.spaceId === "string" ? v.spaceId : "platform" };
  } catch {
    return { ...DEFAULT_SCOPE };
  }
}

export function saveScope(scope: PortalScope): void {
  try {
    globalThis.localStorage?.setItem(SCOPE_KEY, JSON.stringify(scope));
  } catch {
    /* storage blocked — the in-memory state still drives this session */
  }
}
